import React from 'react';
import {
  Container,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Avatar,
  Chip,
} from '@mui/material';
import { keyframes } from '@emotion/react';
import BuildIcon from '@mui/icons-material/Build';
import SecurityIcon from '@mui/icons-material/Security';
import CloudIcon from '@mui/icons-material/Cloud';
import EngineeringIcon from '@mui/icons-material/Engineering';
import RouterIcon from '@mui/icons-material/Router';
import PersonIcon from '@mui/icons-material/Person';

const rotateCW  = keyframes`from{transform:rotate(0deg)}to{transform:rotate(360deg)}`;
const rotateCCW = keyframes`from{transform:rotate(0deg)}to{transform:rotate(-360deg)}`;
const glowPulse = keyframes`
  0%,100%{box-shadow:0 0 20px rgba(255,107,107,0.2),inset 0 0 15px rgba(255,107,107,0.05);border-color:rgba(255,107,107,0.3)}
  50%    {box-shadow:0 0 50px rgba(255,107,107,0.4),0 0 80px rgba(255,107,107,0.15),inset 0 0 30px rgba(255,107,107,0.1);border-color:rgba(255,107,107,0.6)}
`;
const scaleBreath = keyframes`0%,100%{transform:scale(1)}50%{transform:scale(1.06)}`;
const floatBadge  = keyframes`0%,100%{transform:translateY(0)}50%{transform:translateY(-7px)}`;
const orbitPulse  = keyframes`
  0%,100%{opacity:0.7;box-shadow:0 0 4px 1px rgba(255,107,107,0.7)}
  50%    {opacity:1;box-shadow:0 0 9px 3px rgba(255,107,107,1),0 0 16px 2px rgba(255,217,61,0.35)}
`;
const iconFloat = keyframes`
  0%,100%{transform:translateY(0) scale(1);filter:drop-shadow(0 0 8px rgba(255,107,107,0.5))}
  50%    {transform:translateY(-5px) scale(1.08);filter:drop-shadow(0 0 20px rgba(255,107,107,0.9)) drop-shadow(0 0 36px rgba(255,217,61,0.35))}
`;

const values = [
  {
    icon: EngineeringIcon,
    title: '16+ Years Enterprise Experience',
    description: 'Hands-on IT experience across global enterprises including Société Générale Bank, Nestlé, and Velonex Technologies.',
  },
  {
    icon: SecurityIcon,
    title: 'Cybersecurity Certified',
    description: 'CompTIA CySA+ certified. Experienced in Zero Trust architecture, Cisco ISE/NAC, firewall configuration, and network security.',
  },
  {
    icon: CloudIcon,
    title: 'Microsoft & Cloud Expert',
    description: 'Deep expertise in Microsoft Azure, Active Directory, Microsoft 365, Intune/Endpoint Manager, and hybrid cloud environments.',
  },
  {
    icon: BuildIcon,
    title: 'End-to-End IT Solutions',
    description: 'From hardware repair and OS deployment to enterprise network administration and VoIP — one trusted professional for all your IT needs.',
  },
];

function AboutUs() {
  return (
    <Box sx={{ bgcolor: 'background.default', minHeight: '100vh', pt: 12, pb: 8 }}>

      {/* ── Hero ── */}
      <Container>
        <Grid container spacing={4} alignItems="center" sx={{ mb: 10 }}>

          {/* Text side */}
          <Grid item xs={12} md={7}>
            <Typography variant="overline" sx={{ color: 'primary.main', fontWeight: 600, letterSpacing: 3, fontSize: '0.8rem', display: 'block', mb: 1.5 }}>
              Lynn, MA · Est. 2008
            </Typography>
            <Typography
              variant="h2"
              component="h1"
              className="gradient-text"
              sx={{ mb: 2.5, fontWeight: 800, lineHeight: 1.2 }}
            >
              About Akdme Tech
            </Typography>
            <Typography variant="h6" sx={{ color: 'text.secondary', mb: 3, maxWidth: 520, lineHeight: 1.85, fontWeight: 400 }}>
              Seasoned IT specialist with 16+ years of enterprise experience — delivering expert IT services
              to homes and small businesses in the Lynn, MA area.
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {['CompTIA CySA+', 'Microsoft 365', 'Azure / Entra ID', 'Cisco ISE', '16+ Years'].map((tag) => (
                <Chip key={tag} label={tag} size="small" sx={{ background: 'rgba(255,107,107,0.08)', border: '1px solid rgba(255,107,107,0.2)', color: 'rgba(255,232,232,0.8)', fontSize: '0.78rem' }} />
              ))}
            </Box>
          </Grid>

          {/* Decorative circle — hidden on mobile */}
          <Grid item xs={12} md={5} sx={{ display: { xs: 'none', md: 'flex' }, justifyContent: 'center' }}>
            <Box sx={{ position: 'relative', width: 280, height: 280 }}>

              {/* Outer dashed orbit */}
              <Box sx={{
                position: 'absolute', top: '50%', left: '50%',
                width: 280, height: 280, ml: '-140px', mt: '-140px',
                borderRadius: '50%',
                border: '1.5px dashed rgba(255,107,107,0.22)',
                animation: `${rotateCW} 28s linear infinite`,
                zIndex: 1,
              }}>
                {[
                  { top: '-4px', left: '50%', transform: 'translateX(-50%)' },
                  { bottom: '-4px', left: '50%', transform: 'translateX(-50%)' },
                  { left: '-4px', top: '50%', transform: 'translateY(-50%)' },
                  { right: '-4px', top: '50%', transform: 'translateY(-50%)' },
                ].map((pos, i) => (
                  <Box key={i} sx={{
                    position: 'absolute', ...pos,
                    width: 8, height: 8, borderRadius: '50%',
                    background: 'radial-gradient(circle, #ff6b6b 0%, rgba(255,107,107,0.2) 100%)',
                    animation: `${orbitPulse} 2.4s ease-in-out infinite`,
                    animationDelay: `${i * 0.6}s`,
                  }} />
                ))}
              </Box>

              {/* Radar sweep */}
              <Box sx={{
                position: 'absolute', top: '50%', left: '50%',
                width: 240, height: 240, ml: '-120px', mt: '-120px',
                borderRadius: '50%', overflow: 'hidden', zIndex: 1, pointerEvents: 'none',
              }}>
                <Box sx={{
                  width: '100%', height: '100%',
                  background: 'conic-gradient(from 0deg, transparent 0deg, rgba(255,107,107,0.12) 45deg, transparent 65deg)',
                  animation: `${rotateCW} 5s linear infinite`,
                }} />
              </Box>

              {/* Middle CCW ring */}
              <Box sx={{
                position: 'absolute', top: '50%', left: '50%',
                width: 200, height: 200, ml: '-100px', mt: '-100px',
                borderRadius: '50%',
                border: '1px solid rgba(255,107,107,0.1)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                zIndex: 2,
              }}>
                <Box sx={{
                  position: 'absolute', width: '100%', height: '100%',
                  borderRadius: '50%',
                  border: '1.5px dashed rgba(255,217,61,0.2)',
                  animation: `${rotateCCW} 16s linear infinite`,
                }} />

                {/* Spinning colour ring + inner core */}
                <Box sx={{ position: 'relative', width: 130, height: 130, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Box sx={{
                    position: 'absolute', inset: 0, borderRadius: '50%',
                    background: 'conic-gradient(from 0deg, #ff6b6b, #ffd93d, #6ba3ff, #ff6b6b)',
                    animation: `${rotateCW} 4s linear infinite`,
                    WebkitMaskImage: 'radial-gradient(circle at center, transparent 57px, black 57.5px)',
                    maskImage:       'radial-gradient(circle at center, transparent 57px, black 57.5px)',
                    opacity: 0.75,
                  }} />
                  <Box sx={{
                    width: 116, height: 116, borderRadius: '50%',
                    background: 'linear-gradient(135deg, rgba(255,107,107,0.18) 0%, rgba(255,217,61,0.08) 100%)',
                    border: '1.5px solid rgba(255,107,107,0.32)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    animation: `${glowPulse} 3s ease-in-out infinite, ${scaleBreath} 4s ease-in-out infinite`,
                    position: 'relative', zIndex: 1,
                  }}>
                    <PersonIcon sx={{ fontSize: 46, color: 'primary.main', animation: `${iconFloat} 3s ease-in-out infinite` }} />
                  </Box>
                </Box>
              </Box>

              {/* Floating badges */}
              {[
                { icon: SecurityIcon, top: '-8%',  right: '2%',  left: undefined, bottom: undefined, label: 'CySA+ Certified', delay: '0s' },
                { icon: CloudIcon,    bottom: '8%', right: '-12%',left: undefined, top: undefined,    label: 'Azure / M365',    delay: '1s' },
                { icon: RouterIcon,   top: '30%',  left: '-16%', right: undefined, bottom: undefined, label: 'Networking',      delay: '2s' },
              ].map((b, i) => (
                <Box key={i} sx={{
                  position: 'absolute',
                  top: b.top, bottom: b.bottom, left: b.left, right: b.right,
                  background: 'rgba(28,34,36,0.95)',
                  border: '1px solid rgba(255,107,107,0.25)',
                  borderRadius: '10px', px: 1.4, py: 0.7,
                  display: 'flex', alignItems: 'center', gap: 0.7, zIndex: 10,
                  animation: `${floatBadge} 3s ease-in-out infinite`,
                  animationDelay: b.delay,
                }}>
                  {React.createElement(b.icon, { sx: { fontSize: 13, color: 'primary.main' } })}
                  <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: '0.68rem', letterSpacing: 0.4 }}>{b.label}</Typography>
                </Box>
              ))}
            </Box>
          </Grid>
        </Grid>

        {/* Our Values */}
        <Box sx={{ mb: 12 }}>
          <Typography
            variant="h3"
            sx={{
              mb: 6,
              textAlign: 'center',
              fontWeight: 700,
            }}
          >
            Our Values
          </Typography>
          <Grid container spacing={4}>
            {values.map((value, index) => {
              const Icon = value.icon;
              return (
                <Grid item xs={12} sm={6} md={3} key={index}>
                  <Card
                    sx={{
                      height: '100%',
                      background: 'rgba(255, 255, 255, 0.05)',
                      backdropFilter: 'blur(10px)',
                      border: '1px solid rgba(255, 255, 255, 0.1)',
                      transition: 'transform 0.3s ease-in-out',
                      '&:hover': {
                        transform: 'translateY(-5px)',
                      },
                    }}
                  >
                    <CardContent sx={{ textAlign: 'center', p: 4 }}>
                      <Icon
                        sx={{
                          fontSize: 48,
                          mb: 2,
                          color: '#FF6B6B',
                        }}
                      />
                      <Typography
                        variant="h5"
                        component="h3"
                        sx={{ mb: 2, fontWeight: 600 }}
                      >
                        {value.title}
                      </Typography>
                      <Typography variant="body1" color="text.secondary">
                        {value.description}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        </Box>

        {/* Owner / Expert Section */}
        <Box>
          <Typography
            variant="h3"
            sx={{ mb: 6, textAlign: 'center', fontWeight: 700 }}
          >
            Your IT Expert
          </Typography>
          <Grid container justifyContent="center">
            <Grid item xs={12} sm={8} md={5}>
              <Card
                sx={{
                  background: 'rgba(255, 255, 255, 0.05)',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  transition: 'transform 0.3s ease-in-out',
                  '&:hover': { transform: 'translateY(-5px)' },
                }}
              >
                <CardContent sx={{ textAlign: 'center', p: 4 }}>
                  <Avatar
                    sx={{
                      width: 120,
                      height: 120,
                      mx: 'auto',
                      mb: 2,
                      border: '4px solid',
                      borderColor: 'primary.main',
                      bgcolor: '#FF6B6B',
                      fontSize: '2.5rem',
                      fontWeight: 700,
                    }}
                  >
                    AA
                  </Avatar>
                  <Typography variant="h4" component="h3" sx={{ mb: 1, fontWeight: 700 }}>
                    Abderrazak Agourram
                  </Typography>
                  <Typography variant="subtitle1" sx={{ mb: 2, color: 'primary.main', fontWeight: 600 }}>
                    Founder & IT Engineer
                  </Typography>
                  <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                    CompTIA CySA+ certified IT professional with 16+ years of enterprise experience.
                    Specializing in network administration, Microsoft Azure & 365, cybersecurity,
                    hardware repair, and VoIP systems. Previously served enterprises including
                    Société Générale Bank and Velonex Technologies.
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'center' }}>
                    {['CompTIA CySA+', 'Microsoft Azure', 'Microsoft 365', 'Cisco CUCM', 'VMware', 'Active Directory', 'Intune', 'Network Security'].map((skill) => (
                      <Chip
                        key={skill}
                        label={skill}
                        size="small"
                        sx={{ bgcolor: 'rgba(255,107,107,0.15)', color: '#FF6B6B', border: '1px solid rgba(255,107,107,0.3)' }}
                      />
                    ))}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Box>
      </Container>
    </Box>
  );
}

export default AboutUs;
